
import React from 'react';
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { SettingsSectionProps } from '../types';

// Define difficulty types
export type DifficultyLevel = "Easy" | "Normal" | "Hard" | "Expert";

const DIFFICULTY_LABELS: DifficultyLevel[] = ['Easy', 'Normal', 'Hard', 'Expert'];

const DIFFICULTY_DESCRIPTIONS = {
  'Easy': 'More frequent challenges with higher success rates',
  'Normal': 'Balanced challenge frequency and success rates',
  'Hard': 'Challenging decisions with lower success rates',
  'Expert': 'High-risk decisions with significant consequences'
};

const DifficultySettings = ({ localSettings, setLocalSettings }: SettingsSectionProps) => {
  const getDifficultyIndex = () => {
    return DIFFICULTY_LABELS.indexOf(localSettings.difficulty);
  };

  const getDifficultyFromSlider = (value: number): DifficultyLevel => {
    return DIFFICULTY_LABELS[value];
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Label htmlFor="difficulty">Difficulty</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[200px]">Affects challenge difficulty, decision outcomes, and KPI targets</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <span className="font-medium text-sm">
          {localSettings.difficulty}
        </span>
      </div>
      
      <Slider 
        id="difficulty"
        min={0}
        max={3}
        step={1}
        value={[getDifficultyIndex()]}
        onValueChange={(value) => 
          setLocalSettings({
            ...localSettings,
            difficulty: getDifficultyFromSlider(value[0])
          })
        }
      />
      
      <p className="text-sm text-muted-foreground">
        {DIFFICULTY_DESCRIPTIONS[localSettings.difficulty]}
      </p>
    </div>
  );
};

export default DifficultySettings;
